import { IRetentionPeriod } from "../Core/ViewModels/Company/Settings/RetentionSettings";
import { Option } from "react-dropdown";
import {
    AuthMethodDialogConstants,
    AuthMethodLabels,
    DocumentRequestConstants,
    DropOffLinkTypeLabels,
    LocalStorageConstants,
    MessageTitleConstants,
    RetentionPeriodConstants,
    SortAndFilterConstants,
    UserGroupConstants,
} from "../../src/Core/Common/Constants";
import {
    AuthenticationMethod,
    FolderColumnName,
    FolderType,
    TemplateColumnName,
    ListingStatus,
    DropOffLinkType,
    ShareFolderTypes,
    MyFilesFileType,
    MessageType,
} from "../../src/Core/Common/Enums";
import { IDelegateeUser } from "../../src/Core/ViewModels/User/UserViewModel";
import { IFolderModel, IMessageFilesViewModel, IMyFilesDownloadPayload } from "../../src/Core/ViewModels/Folder/FolderModel";
import { appInsightsData } from "./AppInsightsData";
import { store } from "../../src/store";
import { IRecipientModel } from "../../src/Core/ViewModels/Message/RecipientModel";
import { IMenuSection } from "../../src/Core/ViewModels/SideMenu/SideMenuModel";
import { ResourceIdLocators } from "./ResourceIdLocators";
import { ShareFolderTypeLabels, notAvailableRoutes } from "./Constants";
import { IUserProfile } from "../../src/Core/ViewModels/Profile/ProfileViewModel";
import { invalidFileNameRegex } from "./Validations";
import { IOfficeLocation, ISupportContact } from "../reducers/adminSettingsReducer";
import { OverlayTrigger, Popover } from "react-bootstrap";
import React from "react";
import DOMPurify from "dompurify";
import { IUserSettingsStore } from "src/reducers/userSettingsReducer";
import { IMyFilesFileModel } from "src/Core/ViewModels/File/MyFilesFileModel";
import { IDocumentRequestFile } from "src/Core/ViewModels/documentRequest/DocumentRequestModel";


export const getRetentionOptions = (retentionPeriods: IRetentionPeriod[]): Option[] => {
    const retentionOptions: Option[] = [];

    if (retentionPeriods?.length > 0)
        retentionPeriods.forEach(x => {
            let label = "";
            if (x.days < RetentionPeriodConstants.DAYS365) {
                label = x.days + (x.days === 1 ? RetentionPeriodConstants.DAY : RetentionPeriodConstants.DAYS);
            } else if (x.days === RetentionPeriodConstants.DAYS365) {
                label = RetentionPeriodConstants._1YEAR;
            } else if (x.days === RetentionPeriodConstants.DAYS1095) {
                label = RetentionPeriodConstants._3YEARS;
            } else if (x.days === RetentionPeriodConstants.DAYS2555) {
                label = RetentionPeriodConstants._7YEARS;
            }

            retentionOptions.push({
                label,
                value: x.days.toString(),
                data: { "isDefault": x.isDefault ? "true" : "false" }
            });
        });

    return retentionOptions;
};

export const getFormattedRetentionString = (retentionPeriod: number) => {
    if (retentionPeriod < RetentionPeriodConstants.DAYS365) {
        return retentionPeriod + (retentionPeriod === 1 ? " Day" : " Days");
    } else if (retentionPeriod === RetentionPeriodConstants.DAYS365) {
        return RetentionPeriodConstants._1YEAR;
    } else if (retentionPeriod === RetentionPeriodConstants.DAYS1095) {
        return RetentionPeriodConstants._3YEARS;
    } else if (retentionPeriod === RetentionPeriodConstants.DAYS2555) {
        return RetentionPeriodConstants._7YEARS;
    } else {
        return retentionPeriod + RetentionPeriodConstants.DAYS;
    }
};

export const getAuthenticationMethodDialogDimensions = () => {
    return Office.context.platform === Office.PlatformType.OfficeOnline
        ? { height: 84, width: 60, displayInIframe: true }
        : { height: 80, width: 60, displayInIframe: true };
};

export const getDialogDimensions = (displayInIframe = true) => {
    let dimensions = { height: 70, width: 90, displayInIframe };
    if (screen.height <= 768) {
        dimensions =
            Office.context.platform === Office.PlatformType.OfficeOnline
                ? { height: 84, width: 90, displayInIframe }
                : { height: 84, width: 90, displayInIframe };
    } else if (screen.height > 768 && screen.height <= 864) {
        dimensions =
            Office.context.platform === Office.PlatformType.OfficeOnline
                ? { height: 70, width: 90, displayInIframe }
                : { height: 70, width: 90, displayInIframe };
    } else if (screen.height > 864 && screen.height <= 1080) {
        dimensions =
            Office.context.platform === Office.PlatformType.OfficeOnline
                ? { height: 70, width: 80, displayInIframe }
                : { height: 70, width: 80, displayInIframe };
    }

    return dimensions;
};

export const getMessageHistoryDialogDimensions = () => {
    return Office.context.platform === Office.PlatformType.OfficeOnline
        ? { height: 80, width: 90, displayInIframe: true }
        : { height: 80, width: 90, displayInIframe: true };
};

export const getLogoutDialogDimensions = () => {
    return Office.context.platform === Office.PlatformType.OfficeOnline
        ? { height: 40, width: 60, displayInIframe: false }
        : { height: 40, width: 60, displayInIframe: true };
};

export function convertHtmlStringToText(html: string) {
    const tempDivElement = document.createElement("div");

    // Sanitize the input string to prevent XSS attacks
    html = DOMPurify.sanitize(html);

    tempDivElement.innerHTML = html;
    return tempDivElement.textContent || tempDivElement.innerText || "";
}

export const getAuthMethodCardTitle = (element: string) => {
    switch (AuthenticationMethod[element]) {
        case AuthenticationMethod.QUESTIONANSWER:
            return AuthMethodDialogConstants.QNA_CARD_TITLE;

        case AuthenticationMethod.ACCESSCODE:
            return AuthMethodDialogConstants.ACCESS_CODE_CARD_TITLE;

        // case AuthenticationMethod.LOGINPASSWORD:
        //     return AuthMethodDialogConstants.LOGIN_CARD_TITLE;
    }
};

export function getImageInitials(firstName: string, lastName?: string): string {
    const matches = `${firstName.charAt(0)}${lastName?.charAt(0) || ""}`;
    return matches.toUpperCase();
}

export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export const getMailboxUserDataFromLocal = () => {
    const data = sessionStorage.getItem(LocalStorageConstants.otherUser);
    return data ? (JSON.parse(data) as IDelegateeUser) : null;
};

export const getMailboxUserEmailId = () => {
    const data = store.getState().commonState.mailboxUserData;
    if (data) {
        return data.metadata;
    } else {
        return "";
    }
};

export const getMailboxUserEmailText = () => {
    const data = getMailboxUserDataFromLocal();
    if (data) {
        return data.email;
    } else {
        return "";
    }
};

export const getRecipientsDisplayData = (
    firstName: string | undefined,
    lastName: string | undefined,
    email: string | undefined
): string => {
    if (firstName || lastName) {
        return `${firstName ?? ""} ${lastName ?? ""}`;
    } else if (email) {
        return email;
    } else {
        return "";
    }
};

export const getDynamicMaxHeightForAttachments = (attachmentsLength: number, messageLength: number): string => {
    if (attachmentsLength > 0 && messageLength <= 100) {
        return "60vh";
    } else if (messageLength >= 100 && attachmentsLength > 0) {
        return "90px";
    } else {
        return "none";
    }
};

export const getDynamicMaxHeightForMessage = (attachmentsLength: number, messageLength: number): string => {
    if (attachmentsLength > 0 && messageLength < 200) {
        return "none";
    } else if (messageLength >= 200 && attachmentsLength > 0) {
        return "40vh";
    } else if (messageLength >= 200 && attachmentsLength === 0) {
        return "65vh";
    }
};

export const getFolderOptions = (folders: IFolderModel[]): Option[] => {
    return folders.map(f => {
        return { label: f.path, value: f.folderId.toString() };
    });
};

export const getFolderModelColumns = (column?: string): FolderColumnName => {
    let enumVal;
    switch (column) {
        case "folderName":
            enumVal = FolderColumnName.FOLDERNAME;
            break;
        case "expiresDays":
            enumVal = FolderColumnName.EXPIRESDAYS;
            break;
        default:
            enumVal = FolderColumnName.FOLDERNAME;
            break;
    }
    return enumVal;
};

export const convertTextToHtml = (str: string) => {
    // Sanitize the input string to prevent XSS attacks
    str = DOMPurify.sanitize(str);

    //This method replaces /n character with <br>
    str = str.replace(/(?:\r\n|\r|\n)/g, "<br>");
    return str;
};

export const getCompanyAuthMethods = () => {
    const methods = [];
    methods.push({
        value: AuthenticationMethod.ACCESSCODE.toString(),
        label: AuthMethodLabels.ACCESSCODE,
        key: AuthenticationMethod.ACCESSCODE,
    });
    methods.push({
        value: AuthenticationMethod.QUESTIONANSWER.toString(),
        label: AuthMethodLabels.QUESTIONANSWER,
        key: AuthenticationMethod.QUESTIONANSWER,
    });
    // methods.push({
    //     value: AuthenticationMethod.LOGINPASSWORD.toString(),
    //     label: AuthMethodLabels.LOGINPASSWORD,
    //     key: AuthenticationMethod.LOGINPASSWORD,
    // });
    return methods;
};

export const getDropOffLinkTypes = () => {
    const linkTypes = [];
    linkTypes.push({
        value: DropOffLinkType.COMPANY.toString(),
        label: DropOffLinkTypeLabels.COMPANY,
        key: DropOffLinkType.COMPANY,
    });
    linkTypes.push({
        value: DropOffLinkType.PERSONAL.toString(),
        label: DropOffLinkTypeLabels.PERSONAL,
        key: DropOffLinkType.PERSONAL,
    });
    linkTypes.push({
        value: DropOffLinkType.NONE.toString(),
        label: DropOffLinkTypeLabels.NONE,
        key: DropOffLinkType.NONE,
    });
    return linkTypes;
};

export const getMiliSecondsFromMinutes = (minutes: number): number => {
    return 1000 * 60 * minutes;
};

export const getHeaderTitleFromPath = (pathname: string) => {
    switch (pathname.toLowerCase()) {
        case "/sent":
            return "Sent";
        case "/spam":
            return "Spam";
        case "/myfiles":
            return "My Files";
        case "/unlock":
            return "Unlock";
        case "/compose":
            return "Compose";
        case "/request-documents":
            return "Request Documents";
        case "/inbox":
        default:
            return "Inbox";
    }
};

export const getPageTitleFromFolderType = (folderType: FolderType) => {
    switch (folderType) {
        case FolderType.INBOX:
            return appInsightsData.MailBox.Inbox.PageTitle;
        case FolderType.SENT:
            return appInsightsData.MailBox.Sent.PageTitle;
        case FolderType.SPAM:
            return appInsightsData.MailBox.Spam.PageTitle;
        case FolderType.ARCHIVE:
            return appInsightsData.Folders.MyFiles.PageTitle;
        default:
            return "";
    }
};

export const viewingOthersInboxLabel = (firstName: string, lastName: string) =>
    `You are viewing the mailbox of ${firstName ? firstName : ""} ${lastName ? lastName : ""}`;
export const getMessageListWrapperHeight = (mailboxData: IDelegateeUser | null, isArchive: boolean) => {
    if (isArchive) {
        return "calc(100vh - 260px)";
    }

    return mailboxData ? "calc(100vh - 90px)" : "calc(100vh - 110px)";
};
export const getUnlockTableWrapperHeight = (mailboxData: IDelegateeUser | null) =>
    mailboxData ? "calc(100vh - 130px)" : "calc(100vh - 110px)";

export const checkInputIsBlank = (value: string) => {
    if (value.length === 0 || value.trim().length === 0) {
        return false;
    }
    return true;
};

export const getUserIdFromEmail = (data: IRecipientModel[], email: string) => {
    let userId = 0;
    for (let i = 0; i < data.length; i++) {
        if (data[i].emailAddress === email && data[i].userId) {
            userId = data[i].userId ?? 0;
            break;
        }
    }
    return userId;
};
export const toogleMessageHistoryDialog = (caption: string) => {
    let route = "";
    switch (caption) {
        case "Sent Messages":
            route = "sent-item-history";
            break;
        case "Received Messages":
            route = "receive-item-history";
            break;
        case "Document Requests":
            route = "document-request-history";
            break;
    }
    const url = `${window.location.origin}/${route}`;
    Office.context.ui.displayDialogAsync(url, getMessageHistoryDialogDimensions(), asyncResult => {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => dialog.close());
    });
};

export const getWebSocketGroup = (companyId: number) => {
    const group = "00000000" + companyId;
    return "sse" + group.substring(group.length - 8);
};

export const getWebAddinSideMenuItems = (sideMenuData: IMenuSection[]) => {
    // Remove unwanted routes from SideMenu res which r not available for Web Add-in
    // For menus which have dialog the route should be ""

    sideMenuData &&
        sideMenuData.length > 0 &&
        sideMenuData.forEach(section => {
            section.items.forEach((menu, menuIndex) => {
                if (notAvailableRoutes.includes(menu.caption)) {
                    section.items.splice(menuIndex, 1);
                }
            });
        });
    return sideMenuData;
};

export const checkIfPermissionIsAllowed = (permissionsList: string[], resourceId: string) => {
    return permissionsList.find(permission => permission === resourceId) ? true : false;
};

export const checkIfHeaderRestrictedPathname = (pathname: string) => {
    return !pathname.includes("my-settings") && !pathname.includes("support") && !pathname.includes("compose");
};

export function IsAdminUser(user: any) {
    return user?.profile?.role?.split(",")?.includes(UserGroupConstants.ADMIN) || false;
}

export function IsExchangeUser(user: any) {
    return user?.profile?.role?.split(",")?.includes("Exchange") || false;
}

export function getResourceLocators(type: number) {
    switch (type) {
        case FolderType.INBOX:
            return ResourceIdLocators.Inbox;
        case FolderType.ARCHIVE:
            return ResourceIdLocators.MyFiles;
        case FolderType.SENT:
            return ResourceIdLocators.Sent;
        case FolderType.SPAM:
            return ResourceIdLocators.Spam;
    }
}

export const getCookie = (cookieName: string) => {
    const cookieString = document.cookie;
    const cookieArray = cookieString.split(";");
    for (let i = 0; i < cookieArray.length; ++i) {
        if (cookieArray[i].trim().match("^" + cookieName + "=")) {
            return cookieArray[i].replace(`${cookieName}=`, "").trim();
        }
    }
    return null;
};

export const setCookie = (name: string, value: string, days: number) => {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

export const removeCookie = (name: string) => {
    document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
};

export const getTemplateModelColumns = (column?: string): TemplateColumnName => {
    let enumVal;
    switch (column) {
        case "name":
            enumVal = TemplateColumnName.NAME;
            break;
        case "description":
            enumVal = TemplateColumnName.DESCRIPTION;
            break;
        case "createdDate":
            enumVal = TemplateColumnName.CREATEDDATE;
            break;
        case "modifiedDate":
            enumVal = TemplateColumnName.MODIFIEDDATE;
            break;
        default:
            enumVal = TemplateColumnName.NAME;
            break;
    }
    return enumVal;
};

export const getDocumentStatusButtonClass = (documentStatus: ListingStatus) => {
    switch (documentStatus) {
        case ListingStatus.COMPLETED:
            return "document-completed";
        case ListingStatus.FAILED:
            return "document-failed";
        case ListingStatus.OVERDUE:
            return "document-overdue";
        case ListingStatus.PARTIALLY_UPLOADED:
            return "document-partially-uploaded";
        case ListingStatus.PENDING_UPLOAD:
            return "document-pending-upload";
        default:
            return "document-status-button";
    }
};

export const getDocumentStatusButtonCaption = (documentStatus: ListingStatus) => {
    switch (documentStatus) {
        case ListingStatus.COMPLETED:
            return "Completed";
        case ListingStatus.FAILED:
            return "Failed";
        case ListingStatus.OVERDUE:
            return "Overdue";
        case ListingStatus.PARTIALLY_UPLOADED:
            return "Partially Uploaded";
        case ListingStatus.PENDING_UPLOAD:
            return "Pending Upload";
        default:
            return "Default";
    }
};
export const getHeightForDocumentBody = (headerIsBig: boolean, subject: string): string => {
    if (headerIsBig) {
        if (subject.length > 50) {
            return "calc(100vh - 370px)";
        }
        return "calc(100vh - 345px)";
    }
    return "calc(100vh - 305px)";
};

export const enableHeaderAction = (folder: IFolderModel, user: IUserProfile, mailboxData: IDelegateeUser | null) => {
    if (
        isFolderOwner(folder, user, mailboxData) ||
        (folder.permissionGroupId == ShareFolderTypes.EDIT && folder.parentId !== 0 && folder.isPermissiomInherited)
    ) {
        return true;
    } else if (
        (folder.permissionGroupId == ShareFolderTypes.EDIT && folder.parentId === 0) ||
        folder.permissionGroupId == ShareFolderTypes.VIEW
    ) {
        return false;
    } else if (folder.permissionGroupId === ShareFolderTypes.NONE && !folder.isPermissiomInherited) {
        return true;
    }

    return false;
};

export const isFolderOwner = (folder: IFolderModel, user: IUserProfile, mailboxData: IDelegateeUser | null) => {
    if (mailboxData && folder.createdBy === mailboxData.userId) {
        return true;
    } else if (!mailboxData && folder.createdBy === user.userId) {
        return true;
    }
    return false;
};

export const enableEditPermission = (user: IUserProfile, mailboxUserData: IDelegateeUser | null, folder?: IFolderModel) => {
    if (
        !folder ||
        isFolderOwner(folder, user, mailboxUserData) ||
        folder.permissionGroupId == ShareFolderTypes.EDIT
    ) {
        return true;
    } else if (folder.permissionGroupId == ShareFolderTypes.VIEW) {
        return false;
    }
};

export function encode(value: string): string {
    let newlyEncodedString = "";
    for (let i = 0; i < value.length; i++) {
        if (value.charCodeAt(i) > 31 && value.charCodeAt(i) < 127) {
            newlyEncodedString += "{STARTASCII}" + value.charCodeAt(i) + "{ENDASCII}";
        } else {
            newlyEncodedString += value[i];
        }
    }
    return newlyEncodedString;
}

export const getDRSortAndFilterOptions = (text: string, selectedFilterOption: string, selectedSortOption: string) => {
    const filterOption = getSelectedDRFilterOption(selectedFilterOption);
    const sortOption = getSelectedDRSortOption(selectedSortOption);
    switch (text) {
        case SortAndFilterConstants.ALPHABETICAL_ORDER_ASC:
            return {
                sortColumn: "email",
                sortOrder: SortAndFilterConstants.SORT_ORDER_ASC,
                listingStatus: filterOption,
            };

        case SortAndFilterConstants.ALPHABETICAL_ORDER_DESC:
            return {
                sortColumn: "email",
                sortOrder: SortAndFilterConstants.SORT_ORDER_DESC,
                listingStatus: filterOption,
            };

        case SortAndFilterConstants.NEWEST_FIRST_ORDER:
            return {
                sortColumn: "sentDate",
                sortOrder: SortAndFilterConstants.SORT_ORDER_DESC,
                listingStatus: filterOption,
            };

        case SortAndFilterConstants.OLDEST_FIRST_ORDER:
            return {
                sortColumn: "sentDate",
                sortOrder: SortAndFilterConstants.SORT_ORDER_ASC,
                listingStatus: filterOption,
            };
        case SortAndFilterConstants.ALL_STATUSES_ORDER:
            return {
                sortColumn: sortOption.sortColumn,
                sortOrder: sortOption.sortOrder,
                listingStatus: ListingStatus.NONE,
            };
        case SortAndFilterConstants.COMPLETED_ORDER:
            return {
                sortColumn: sortOption.sortColumn,
                sortOrder: sortOption.sortOrder,
                listingStatus: ListingStatus.COMPLETED,
            };
        case SortAndFilterConstants.PENDING_UPLOAD_ORDER:
            return {
                sortColumn: sortOption.sortColumn,
                sortOrder: sortOption.sortOrder,
                listingStatus: ListingStatus.PENDING_UPLOAD,
            };
        case SortAndFilterConstants.PARTIALLY_UPLOAD_ORDER:
            return {
                sortColumn: sortOption.sortColumn,
                sortOrder: sortOption.sortOrder,
                listingStatus: ListingStatus.PARTIALLY_UPLOADED,
            };
        case SortAndFilterConstants.OVERDUE_ORDER:
            return {
                sortColumn: sortOption.sortColumn,
                sortOrder: sortOption.sortOrder,
                listingStatus: ListingStatus.OVERDUE,
            };
        default:
            return { sortColumn: sortOption.sortColumn, sortOrder: sortOption.sortOrder, listingStatus: filterOption };
    }
};

const getSelectedDRFilterOption = (text: string) => {
    switch (text) {
        case SortAndFilterConstants.ALL_STATUSES_ORDER:
            return ListingStatus.NONE;
        case SortAndFilterConstants.COMPLETED_ORDER:
            return ListingStatus.COMPLETED;
        case SortAndFilterConstants.PENDING_UPLOAD_ORDER:
            return ListingStatus.PENDING_UPLOAD;
        case SortAndFilterConstants.PARTIALLY_UPLOAD_ORDER:
            return ListingStatus.PARTIALLY_UPLOADED;
        case SortAndFilterConstants.OVERDUE_ORDER:
            return ListingStatus.OVERDUE;
        default:
            return ListingStatus.NONE;
    }
};

const getSelectedDRSortOption = (text: string) => {
    switch (text) {
        case SortAndFilterConstants.ALPHABETICAL_ORDER_ASC:
            return { sortColumn: "email", sortOrder: SortAndFilterConstants.SORT_ORDER_ASC };

        case SortAndFilterConstants.ALPHABETICAL_ORDER_DESC:
            return { sortColumn: "email", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };

        case SortAndFilterConstants.NEWEST_FIRST_ORDER:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };

        case SortAndFilterConstants.OLDEST_FIRST_ORDER:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_ASC };

        default:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };
    }
};

export const getFolderMessageSortOptions = (selectedSortOption: string) => {
    switch (selectedSortOption) {
        case SortAndFilterConstants.ALPHABETICAL_ORDER_ASC:
            return { sortColumn: "senderName", sortOrder: SortAndFilterConstants.SORT_ORDER_ASC };

        case SortAndFilterConstants.ALPHABETICAL_ORDER_DESC:
            return { sortColumn: "senderName", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };

        case SortAndFilterConstants.NEWEST_FIRST_ORDER:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };

        case SortAndFilterConstants.OLDEST_FIRST_ORDER:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_ASC };

        default:
            return { sortColumn: "sentDate", sortOrder: SortAndFilterConstants.SORT_ORDER_DESC };
    }
};

export const getDaysFromRetentionPeriodId = (retentionPeriodId: number, retentionPeriodList: IRetentionPeriod[]): number => {
    const retentionPeriod = retentionPeriodList.find(period => period.retentionId === retentionPeriodId);
    return retentionPeriod ? retentionPeriod.days : 0; // Default case if not found
};

export const getShareFolderOptions = (): Option[] => {
    const options: Option[] = [];
    options.push({
        value: ShareFolderTypes.VIEW.toString(),
        label: ShareFolderTypeLabels.VIEW,
    });
    options.push({
        value: ShareFolderTypes.EDIT.toString(),
        label: ShareFolderTypeLabels.EDIT,
    });
    options.push({
        value: ShareFolderTypes.REMOVE.toString(),
        label: ShareFolderTypeLabels.REMOVE,
    });

    return options;
};

export const getShareFolderOptionValue = (permissionGroupId: ShareFolderTypes): string => {
    switch (permissionGroupId) {
        case ShareFolderTypes.VIEW:
            return ShareFolderTypes.VIEW.toString();
        case ShareFolderTypes.EDIT:
            return ShareFolderTypes.EDIT.toString();
        case ShareFolderTypes.REMOVE:
            return ShareFolderTypes.REMOVE.toString();
        default:
            return "";
    }
};

export const isDelegatedUser = (): boolean => {
    const data = store.getState().commonState.mailboxUserData;
    const user = data ? data : null;
    return user?.email.length > 0 ? true : false;
};
export const getFileName = (name: string): string => {
    return name.replace(invalidFileNameRegex, "_");
};

export const getMessageTitleFromFolderType = (folderType: FolderType) => {
    switch (folderType) {
        case FolderType.INBOX:
            return MessageTitleConstants.TITLE_INBOX;
        case FolderType.SENT:
            return MessageTitleConstants.TITLE_SENT;
        case FolderType.SPAM:
            return MessageTitleConstants.TITLE_SPAM;
        case FolderType.ARCHIVE:
            return MessageTitleConstants.TITLE_ARCHIVE;
        default:
            return "";
    }
};

export const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
};

export function removeSpecialCharacters(input: string): string {
    return input.replace(/[^a-zA-Z0-9_ ]/g, "");
}

// Debounce utility function
export const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout | number;
    return (...args: any[]) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
};

export const addPeriodAtEnd = (text: string): string => {
    if (text[text.length - 1] !== "." || !text.endsWith(".")) {
        return text + ".";
    }
    return text;
};

//saved Message Templates 
export const convertHtmlToText = (htmlString: string): string => {
    let text = htmlString;
    text = text.replace(/&nbsp;/g, " ");
    text = text.replace(/<br>/g, "\n");
    return text;
};

export const convertToHtmlString = (input: string): string => {
    const htmlString = input
        .replace(/ {2,}/g, (match) => "&nbsp;".repeat(match.length))
        .replace(/\n/g, "<br>");
    return htmlString;
};


export const sortLocationsByName = (locations: IOfficeLocation[]): IOfficeLocation[] => {
    return [...locations].sort((a, b) => {
        const nameA = a.name.trim().toLowerCase();
        const nameB = b.name.trim().toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });
};

export const sortContactsByFullName = (contacts: ISupportContact[]): ISupportContact[] => {
    return [...contacts].sort((a, b) => {
        const fullNameA = `${a.firstName.trim()} ${a.lastName.trim()}`.toLowerCase();
        const fullNameB = `${b.firstName.trim()} ${b.lastName.trim()}`.toLowerCase();

        if (fullNameA < fullNameB) return -1;
        if (fullNameA > fullNameB) return 1;
        return 0;
    });
};

export const getFormattedLinkedRecipients = (recipients: string[]) => (
    <span className="linked-email-container">
        <b>Linked Portal Emails: </b>&nbsp;
        <span className="ellipsis" title={recipients[0]}>{recipients[0]}</span>&nbsp;
        {recipients.length > 1 && (
            <OverlayTrigger trigger="click" placement="bottom" overlay={popoverLinkedRecipients(recipients)} rootClose>
                <span className="badge-recipients pointer">+{recipients.length - 1}</span>
            </OverlayTrigger>
        )}
    </span>
);

export const popoverLinkedRecipients = (cell: string[]) => (
    <Popover id="popover-recipients">
        <Popover.Header as="h3">
            {DocumentRequestConstants.LINKED_RECIPIENTS_POPOVER_TITLE}
        </Popover.Header>
        <Popover.Body className="reports-popover-body">
            {cell.map((data, index) => (
                <div key={index}>
                    <ul>
                        <li title={data} >
                            {data}
                        </li>
                    </ul>
                </div>
            ))}
        </Popover.Body>
    </Popover>
);

export const getDefaultTaxYear = (profile: IUserSettingsStore): number => {
    let defaultTaxYear: number;
    if (profile.userSettings?.taxYearSettings?.defaultTaxYear) {
        defaultTaxYear = profile.userSettings?.taxYearSettings?.defaultTaxYear;
    }
    else {
        const currentDate = new Date();
        const mayFirst = new Date(currentDate.getFullYear(), 4, 1); // Month is 0-based, so 4 = May
        defaultTaxYear = currentDate >= mayFirst ? currentDate.getFullYear() : currentDate.getFullYear() - 1;
    }
    return defaultTaxYear;
};

export const getFileViewWrapperHeight = (mailboxData: IDelegateeUser | null) => {
    return mailboxData ? "calc(100vh - 256px)" : "calc(100vh - 240px)";
};

export const getFileViewLoaderHeight = (mailboxData: IDelegateeUser | null) => {
    return mailboxData ? "calc(100vh - 207px)" : "calc(100vh - 191px)";
};

export const getTotalFileSize = (selectedFiles: IMyFilesFileModel[]) => {
    if (selectedFiles?.length) {
        let totalSize = 0;
        selectedFiles.map(file => (totalSize += file.size));
        return totalSize;
    } else return 0;
};


export const updateDocumentRequestPayload = (file: IMyFilesFileModel, group: Record<string, IDocumentRequestFile>) => {
    const key = `${file.documentRequestFileId}_${file.referenceId}`;
    if (!group[key]) {
        group[key] = {
            documentRequestFileId: file.documentRequestFileId,
            name: file.documentName,
            description: "",
            documentRequestId: file.referenceId,
            documentCategoryId: undefined,
            documentCategoryName: file.documentCategory,
            createdDate: file.sentOrReceivedDate,
            uploadedFiles: [],
        };
    }
    group[key].uploadedFiles.push({
        fileName: file.fileName,
        fileType: "",
        fileSize: file.size,
        fileGuid: file.fileGuid,
        isUploaded: file.isUploaded,
        uploadedDate: file.uploadedDate,
        fileId: file.fileId,
    });
};


export const updateMessagePayload = (file: IMyFilesFileModel, messagePayload: IMessageFilesViewModel[]) => {
    if (messagePayload?.length) {
        const hasMessageObj = messagePayload.some(
            item =>
                item.messageGuid === file.referenceGuid && item.messageType == (file.fileType as unknown as MessageType)
        );
        if (hasMessageObj) {
            messagePayload.map(item => {
                if (item.messageGuid === file.referenceGuid) {
                    return item.files.push({
                        fileGuid: file.fileGuid,
                        name: file.fileName,
                        size: file.size,
                        isUploadComplete: file.isUploaded,
                        fileId: file.fileId,
                    });
                } else {
                    return item;
                }
            });
        } else {
            messagePayload.push({
                messageGuid: file.referenceGuid,
                files: [
                    {
                        name: file.fileName,
                        size: file.size,
                        isUploadComplete: file.isUploaded,
                        fileId: file.fileId,
                        fileGuid: file.fileGuid,
                    },
                ],
                senderEmail: file.senderEmail,
                messageSource: file.messageSource,
                messageId: file.referenceId,
                messageType: file.fileType as unknown as MessageType,
            });
        }
    } else {
        messagePayload.push({
            messageGuid: file.referenceGuid,
            files: [
                {
                    name: file.fileName,
                    size: file.size,
                    isUploadComplete: file.isUploaded,
                    fileId: file.fileId,
                    fileGuid: file.fileGuid,
                },
            ],
            senderEmail: file.senderEmail,
            messageSource: file.messageSource,
            messageId: file.referenceId,
            messageType: file.fileType as unknown as MessageType,
        });
    }
};

export const getDownloadPayload = (selectedFiles: IMyFilesFileModel[]): IMyFilesDownloadPayload => {
    const payload: IMyFilesDownloadPayload = {
        messageFiles: [],
        documentRequestFiles: [],
    };
    const groupedDRFiles: Record<string, IDocumentRequestFile> = {};
    selectedFiles.forEach(file => {
        if (file.fileType == MyFilesFileType.DocumentRequest) {
            updateDocumentRequestPayload(file, groupedDRFiles);
        } else if (file.fileType == MyFilesFileType.ReceivedMessage || file.fileType == MyFilesFileType.SentMessage) {
            updateMessagePayload(file, payload.messageFiles);
        }
    });
    payload.documentRequestFiles = Object.values(groupedDRFiles);
    return payload;
};